<template>
  <b-modal ref="modal" lazy size="lg" hide-footer class="text-left">
    <template slot="modal-header">
      <h5 class="modal-title">
        {{
          person.id
            ? `Edit Internal Person ${fullName}`
            : `Create new Internal Person`
        }}
      </h5>
      <div class="pull-right">
        <b-btn variant="default" @click="$refs.modal.hide(true)">
          Cancel
        </b-btn>
        <b-btn variant="info" @click.prevent="save">
          <template v-if="!saveLoading">Save</template>
          <template v-else>
            <b-spinner small></b-spinner>
            <span>Saving...</span>
          </template>
        </b-btn>
      </div>
    </template>

    <b-form>
      <b-form-group
        horizontal
        :invalid-feedback="firstNameInvalidFeedback"
        :state="validateState('first_name')"
        label="First name"
      >
        <b-form-input
          type="text"
          v-model="$v.person.first_name.$model"
          :state="validateState('first_name')"
        >
        </b-form-input>
      </b-form-group>

      <b-form-group
        horizontal
        :invalid-feedback="lastNameInvalidFeedback"
        :state="validateState('last_name')"
        label="Last name"
      >
        <b-form-input
          type="text"
          v-model="$v.person.last_name.$model"
          :state="validateState('last_name')"
        >
        </b-form-input>
      </b-form-group>

      <b-form-group
        horizontal
        :invalid-feedback="phoneInvalidFeedback"
        :state="validateState('phone_num')"
        label="Phone Number"
      >
        <b-form-input
          type="text"
          v-model="$v.person.phone_num.$model"
          :state="validateState('phone_num')"
        >
        </b-form-input>
      </b-form-group>

      <b-form-group
        horizontal
        :invalid-feedback="emailInvalidFeedback"
        :state="validateState('email')"
        label="Email"
      >
        <b-form-input
          type="text"
          v-model="$v.person.email.$model"
          :state="validateState('email')"
        >
        </b-form-input>
      </b-form-group>

      <b-form-group horizontal label="Disabled" id="person-disabled">
        <b-form-checkbox
          switch
          v-model="person.disabled"
          :value="1"
          :unchecked-value="0"
          :disabled="!!person.societies.length"
        >
        </b-form-checkbox>
        <template v-if="!!person.societies.length">
          <b-tooltip ref="tooltip" target="person-disabled" triggers="hover">
            Societies:
            <div
              v-for="society in person.societies"
              :key="`modal_${person.id}_${society.id}`"
            >
              {{ society.name }}
            </div>
          </b-tooltip>
        </template>
      </b-form-group>
    </b-form>
  </b-modal>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { required, maxLength, email, helpers } from 'vuelidate/lib/validators';
import Api from '@/providers/Api';
import { showValidationErrorsAsToasted } from '@/helpers/ToastedHelper';
import {
  PHONE_VALIDATION_MESSAGE,
  PHONE_VALIDATION_RGEXP,
} from '@/helpers/PhoneHelpers';

const phone = helpers.regex('phone', PHONE_VALIDATION_RGEXP);

export default {
  name: 'ModalEditForm',
  mixins: [validationMixin],
  props: {
    initialPerson: {
      validator: (prop) => typeof prop === 'object' || prop === null,
      default: null,
    },
  },
  data() {
    const person = { ...this.initialPerson };
    return {
      saveLoading: false,
      person: {
        id: this.initialPerson ? person.id : null,
        first_name: this.initialPerson ? person.first_name : null,
        last_name: this.initialPerson ? person.last_name : null,
        phone_num: this.initialPerson ? person.phone_num : null,
        email: this.initialPerson ? person.email : null,
        disabled: this.initialPerson && person.disabled ? person.disabled : 0,
        societies: this.initialPerson ? person.societies : [],
      },
    };
  },
  validations: {
    person: {
      first_name: {
        required: required,
        maxLength: maxLength(255),
      },
      last_name: {
        required: required,
        maxLength: maxLength(255),
      },
      phone_num: {
        required: required,
        phone,
      },
      email: {
        required: required,
        email: email,
      },
    },
  },
  mounted() {
    this.showModal();
    this.$root.$on('bv::modal::hide', this.onModalHide);
    this.$root.$on('bv::modal::hidden', this.onModalHidden);
  },
  beforeDestroy() {
    this.$root.$off('bv::modal::hide');
  },
  computed: {
    firstNameInvalidFeedback() {
      if (!this.$v.person.first_name.maxLength) {
        return "Title can't be more than 255 characters.";
      }

      return 'This field is required';
    },
    lastNameInvalidFeedback() {
      if (!this.$v.person.last_name.maxLength) {
        return "Title can't be more than 255 characters.";
      }

      return 'This field is required';
    },
    phoneInvalidFeedback() {
      if (!this.$v.person.phone_num.phone) {
        return PHONE_VALIDATION_MESSAGE;
      }

      return 'This field is required';
    },
    emailInvalidFeedback() {
      if (!this.$v.person.email.email) {
        return 'The Email must be a valid email address.';
      }

      return 'This field is required';
    },
    fullName() {
      return `${this.person.first_name} ${this.person.last_name}`;
    },
  },
  methods: {
    validateState(name) {
      const { $dirty, $error } = this.$v.person[name];
      return $dirty ? !$error : null;
    },
    showModal() {
      this.$refs.modal.show();
    },
    save() {
      this.$v.$touch();

      if (this.$v.$anyError) {
        return;
      }

      this.saveLoading = true;

      const promise = this.person.id
        ? Api.updateInternalPerson(this.person)
        : Api.createInternalPerson(this.person);

      promise
        .then((response) => {
          const person = response.data;
          this.syncPerson(person);
          this.$toasted.success(
            `Person successfully ${this.person.id ? 'updated' : 'created'}.`,
            { duration: 3000 }
          );
          this.$emit('personSaved', person);
        })
        .catch((error) => {
          this.showErrors(error);
        })
        .finally(() => {
          this.saveLoading = false;
        });
    },
    syncPerson(person) {
      this.person.id = person.id;
      this.person.first_name = person.first_name;
      this.person.last_name = person.last_name;
      this.person.phone_num = person.phone_num;
      this.person.email = person.email;
      this.person.disabled = person.disabled;
      this.person.societies = person.societies;
    },
    showErrors(error) {
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.errors
      ) {
        showValidationErrorsAsToasted(this.$toasted, error.response.data.errors);
      } else {
        this.$toasted.error(error.response.data.message, { duration: 3000 });
      }
    },
    onModalHide(bvModalEvent) {
      if (this.saveLoading) {
        bvModalEvent.preventDefault();
      }
    },
    onModalHidden() {
      this.$emit('modalCancel');
    },
  },
};
</script>

<style scoped></style>
